import React from 'react';
import { motion } from 'framer-motion';
import type { ViewType } from '../data/homepageData';
import { CREATOR_HERO, BRAND_HERO } from '../data/homepageData';

interface HeroSectionProps {
  view: ViewType;
}

/**
 * Full-viewport hero section with animated orb backgrounds, badge,
 * headline, subtitle, and dual CTA buttons.
 * The orb animations are handled by CSS keyframes in homepage.css.
 */
const HeroSection: React.FC<HeroSectionProps> = ({ view }) => {
  const data = view === 'creator' ? CREATOR_HERO : BRAND_HERO;

  return (
    <section
      className="relative flex justify-center text-center overflow-hidden pt-8 pb-32"
    >
      {/* Orb + grid background layer */}
      <div className="absolute inset-0 overflow-hidden z-0" aria-hidden="true">
        <div className="cv-hero-orb cv-hero-orb-1" />
        <div className="cv-hero-orb cv-hero-orb-2" />
        <div className="cv-hero-orb cv-hero-orb-3" />
        <div className="cv-hero-grid" />
      </div>

      {/* Content */}
      <div className="relative z-[2] max-w-[900px] px-6">
        {/* Badge */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
          className="inline-flex items-center gap-2 mb-8"
          style={{
            padding: '6px 16px',
            borderRadius: 'var(--radius-full)',
            background: 'rgba(168, 85, 247, 0.08)',
            border: '1px solid rgba(168, 85, 247, 0.2)',
            fontSize: '13px',
            fontWeight: 500,
            color: 'var(--cv-accent)',
            letterSpacing: '0.02em',
          }}
        >
          <span className="cv-badge-dot" />
          {data.badge}
        </motion.div>

        {/* Headline */}
        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
          className="font-extrabold tracking-tight mb-6"
          style={{
            fontFamily: 'var(--font-display)',
            fontSize: 'var(--text-hero)',
            lineHeight: 1.05,
            letterSpacing: '-0.03em',
          }}
        >
          <span>{data.headline[0]}</span>
          <br />
          <span className="cv-text-gradient">{data.headline[1]}</span>
        </motion.h1>

        {/* Subtitle */}
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
          className="mx-auto mb-10"
          style={{
            fontSize: 'var(--text-lg)',
            color: 'var(--cv-text-muted)',
            maxWidth: '600px',
            lineHeight: 1.6,
          }}
        >
          {data.subtitle}
        </motion.p>

        {/* CTA group */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3, ease: [0.16, 1, 0.3, 1] }}
          className="cv-hero-cta-group flex items-center justify-center gap-4 flex-wrap"
        >
          <a
            href={data.ctaLink}
            className="cv-btn-primary"
            style={{ padding: '16px 36px', fontSize: '16px' }}
          >
            {data.cta}
            <svg
              width={16}
              height={16}
              viewBox="0 0 16 16"
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
              strokeLinecap="round"
              aria-hidden="true"
            >
              <path d="M3 8h10M9 4l4 4-4 4" />
            </svg>
          </a>

          <a
            href={data.secondaryLink}
            className="cv-btn-secondary"
            style={{ padding: '16px 36px', fontSize: '16px' }}
            {...(data.secondaryLink.startsWith('/') && !data.secondaryLink.startsWith('/#')
              ? { target: '_blank', rel: 'noopener noreferrer' }
              : {})}
          >
            {data.secondaryCta}
          </a>
        </motion.div>
      </div>
    </section>
  );
};

export default HeroSection;
